import PlayerComponentWrapper, {PlayerComponentHookType} from '../wrappers/PlayerComponentWrapper';
import PluginManager from '../../plugins/PluginManager';

import EventManager from "../../communication/EventManager";
import hookManagerInstance from "../../communication/HookManager";
import TechManager from "../../tech/TechManager";
import Dom from "../../utils/custom/Dom"
import mergeOptions from "../../utils/wrappers/merge-options";
import Constants from "../../utils/Constants";

// LOAD SASS
require('sass/base.scss');

// IMPORT SWF FOR FLASH FALLBACK
require('file-loader?name=assets/videojs.swf!videojs-swf/dist/video-js.swf');

// INCLUDE polyfills
import "../../utils/Pollyfills"
import ComponentManager from "../ComponentManager";
import EventUtilWrapper from "../../utils/wrappers/Events";

//Helper
let objectAssign  = require('object-assign');

// custom player hooks
export type SkyClickPlayerHookType =
    'STATIC_ONCE_INIT' | // used to run some static function once
    'READY' // called, when a skyClickPlayer instance is ready


// custom player events
export type SkyClickPlayerEventType =
    'SOURCE_CHANGED' | // custom event: Triggered when source file of video element changed
    'READY_FOR_SYSTEM_PLUGINS' | // custom event: trigger when player is ready before custom code.
    'ENDED' | // alias event for videojs ended
    'READY' | // alias event for videojs ready
    'USER_INACTIVE' | // alias event for videojs userinactive
    'USER_ACTIVE' // alias event for videojs useractive
;

/**
 * Class: SkyClickPlayer: Wrapper of the whole video player
 */

export default class SkyClickPlayer extends PlayerComponentWrapper{

    static hookManagerInstance = hookManagerInstance;

    /**
     * Default player options
     * @type {{controls: boolean}}
     */
    static defaultOptions = {
        // VIDEOJS OPTIONS
        "flash":{
            "swf": Constants.SKYCLICK_SCRIPT_DIR_PATH + "assets/videojs.swf"
        },
        "controls": true,
        "plugins": {
            // default plugins will be loaded in the static methods in plugin decorators + custom plugins
        },
        // CUSTOM OPTIONS
        "preventControlBarDisappearingByMouseOver": true,
        "autoEnableIFrameMode": true
    };

    /**
     * Get default tech order array from videojs
     * @returns {string[]}
     */
    static getDefaultTechOrder() {
        return this.prototype.options_.techOrder;
    }

    /**
     * Create new SkyClick instance
     *
     * It instantiate first a videojs instance.
     * After we extend the instance object with extend the SkyClick Methods + Properties and run our constructor on the created vidoejs instance.
     * This way is the only solution (without touching original videojs code), that we and videojs works always with the same player instance object.
     *
     * Why we can't copy a videojs instance (methods + properties) to our instance?
     * → Because videojs working internally with the instance, they have created and we would working with our instance. That generate a lot of problems.
     *
     * @param selector
     * @param customOptions
     * @param ready
     * @returns {SkyClickPlayer}
     */
    static createInstance(selector:String|HTMLElement,customOptions?:Object, ready?:() => void):SkyClickPlayer{
        // Get merged default + custom options
        let preparedOptions = this.handleCustomOptions(customOptions);

        // Plugin boot level 0: Run hook STATIC_ONCE_INIT only at startup of the first player instance
        hookManagerInstance.getHooks<SkyClickPlayerHookType>('STATIC_ONCE_INIT').forEach(hookFunction => {
            hookFunction();
        });

        // define this plugin, with a plugin object (no properties needed), to autostart
        // See detail of this plugin in this.registerBootSkyClickInstancePlugin
        preparedOptions.plugins['__bootSkyClickInstance'] = {};

        // init videojs instance
        let instance = super.createInstance(this.prepareHtmlStructure(selector), preparedOptions, function(){

                // Plugin boot level 2
                this.pluginManager.bootingDecoratorExecution();
                // Tech boot level 2
                this.techManager.bootingDecoratorExecution();

                // trigger ready event for internal plugins, that wait until player is ready, but must called before custom code (ready hook + event listener + custom plugin)
                (<SkyClickPlayer>instance).eventManager.trigger<SkyClickPlayerEventType>('READY_FOR_SYSTEM_PLUGINS', [instance]);

                // run READY hook
                hookManagerInstance.getHooks<SkyClickPlayerHookType>('READY').forEach(hookFunction => {
                    hookFunction(this);
                });

                // execute the delayed custom auto starts of plugins
                this.executeDelayedCustomAutoStartOfPlugins();

                // run custom ready when all internal ready code is executed
                // Implementation like videojs did it: https://github.com/videojs/video.js/blob/5.x/src/js/component.js#L787
                // That mean the ready function run in the context of the player instance and can access the player with "this" in the ready function.
                ready && ready.call(this);

            });

        // merged instance (videojs instance + skyclick )
        return <SkyClickPlayer>instance;
    }

    static registerBootSkyClickInstancePlugin(){
        // Hack/Trick to extend videojs instance with the SkyClickPlayer class before any plugin started.
        // Then this helper plugin is executed first, because we added two underlines at beginning (that bring this plugin to top of the plugins list)
        // So we are sure, that any plugin has full access to any SkyClickPlayer(new or overwritten) and VideoJS methods + properties.
        let SkyClickPlayerClassReference = this;
        SkyClickPlayer.vjs.plugin('__bootSkyClickInstance',function(options:any){
            let instance = this; // plugin function has as context the player instance

            // extend videojs instance with SkyClick class
            objectAssign(instance, SkyClickPlayerClassReference.prototype);

            let extendedInstance = <SkyClickPlayer>instance;

            // run our constructor on instantiated vidoejs instance.
            SkyClickPlayerClassReference.prototype.constructor.apply(extendedInstance);

            // Load plugin + tech manager
            let pluginManger = extendedInstance.pluginManager = new PluginManager(extendedInstance);
            let techManager = extendedInstance.techManager = new TechManager(extendedInstance);

            // Plugin boot level 1
            pluginManger.bootingDecoratorInstance();
            // Tech boot level 1
            techManager.bootingDecoratorInstance();
        });
    }

    /**
     * Prepare initial html structure for video.js initializing
     * @param selector String|HTMLElement
     * @returns {HTMLElement}
     */
    static prepareHtmlStructure(selector:String|HTMLElement){
        let initialElement:HTMLElement;

        if(typeof selector === 'string'){
            initialElement = Dom.$(`#${selector}`, document.body);
        }else{
            initialElement = <HTMLElement>selector;
        }

        // Add default, needed video.js classes to given initial element
        Dom.addElClass(initialElement,'video-js');
        Dom.addElClass(initialElement,'vjs-default-skin');

        return initialElement;
    }

    /**
     * This function merge the custom options with the default options (this.defaultOptions)
     *
     * Additionally this function prepare the delayed custom auto start of plugins (see this.delayCustomAutoStartOfPlugins)
     * and remove therefore the .plugins property of custom options.
     * @param customOptions
     * @returns {any}
     */
    static handleCustomOptions(customOptions:any){
        // Clone custom options, that we don't remove the plugins object overall for the customOptions
        // Then the custom plugins option will be used later in this.delayCustomAutoStartOfPlugins
        let clonedCustomOptions = mergeOptions({}, customOptions);
        delete (<any>clonedCustomOptions).plugins;

        // Save full custom options in the preparedOptions, because we need it in the this.delayCustomAutoStartOfPlugins function again.
        (<any>clonedCustomOptions).customOptions = customOptions;

        // extend options with our defaults
        // the first empty object make sure, that we don't extend this.defaultOptions with custom Options
        let preparedOptions = mergeOptions({}, this.defaultOptions, clonedCustomOptions);

        return preparedOptions;
    }

    /**
     * This function delayed all custom auto starts of plugins
     *
     * The custom auto starts are all plugins, they are not used for the core SkyClick Player.
     * That mean all options.plugins.XXX entries, they come from outside
     *
     * This function remember the custom auto starts in a extra sub object of options
     * (options.delayedAutoStartOfPluginsStorage)
     * When the complete core of SkyClick core is booted, we start this custom auto starts
     * (before send the ready event to the outside)
     *
     * When from outside came a overwriting of core plugin options, we merge it.
     * The plugin options properties from outside has higher priority.
     * That mean this custom plugin option properties overwrite the default plugin options.
     *
     * @param usedOptions Used options to initialize videojs player
     * @param customOptions Custom options, they we get from outside by SkyClick.setup call
     * @returns {any}
     */
    static delayCustomAutoStartOfPlugins(usedOptions:any,){

        // load cached custom options (happen by this.handleCustomOptions) of usedOptions and remove it
        let customOptions:any = (<any>usedOptions).customOptions;
        delete (<any>usedOptions).customOptions;

        let usedPluginAutoStart = usedOptions.plugins;
        let customPluginAutoStart = customOptions.plugins;

        usedOptions.delayedAutoStartOfPluginsStorage = {};

        for (let pluginName in customPluginAutoStart) {
            if(typeof usedPluginAutoStart[pluginName] == 'undefined'){
                // remember custom auto start of plugins
                usedOptions.delayedAutoStartOfPluginsStorage[pluginName] = customPluginAutoStart[pluginName];
            }else{
                // merge existing plugin options with custom options
                usedPluginAutoStart[pluginName] = mergeOptions(usedPluginAutoStart[pluginName],customPluginAutoStart[pluginName]);
            }
        }

        return usedOptions;
    }

    /**
     * Start delayed custom auto starts of plugins
     *
     * See more information in SkyClickPlayer.delayCustomAutoStartOfPlugins function
     */
    protected executeDelayedCustomAutoStartOfPlugins(){
        let playerPluginsOptions = this.getOptions().plugins;
        let delayedAutoStartOfPluginsStorage = this.getOptions().delayedAutoStartOfPluginsStorage ;

        // execute delayed custom auto start of plugins & added temporarily removed plugin options again to player options object
        for (let pluginName in delayedAutoStartOfPluginsStorage) {
            let pluginOptions = delayedAutoStartOfPluginsStorage[pluginName];

            // bring back removed plugin options to player option object again
            playerPluginsOptions[pluginName] = pluginOptions;

            // execute plugin
            (<any>this)[pluginName](pluginOptions);
        }
    }




    public pluginManager:PluginManager;

    public techManager:TechManager;

    public eventManager:EventManager;

    public iFrameMode:boolean = false; // Is active, when player is inside iFrame

    /**
     * Overwrite videojs options() method, because this method is depracted (will be removed in videojs v6.0)
     */
    public getOptions(){
        return this.options_;
    }

    /**
     * Alias event for system events
     *
     * @example
     * {
     *      {'systemevent':'aliasevent'}
     * }
     */
    protected get systemEventAliases():Object{
        return {
            'ended' : 'ENDED',
            'ready' : 'READY',
            'userinactive' : 'USER_INACTIVE',
            'useractive' : 'USER_ACTIVE'
        };
    }

    /**
     * Create new SkyPlayer instance
     */
    constructor(selector:String){
        super(selector);
        // init eventManager
        this.eventManager = new EventManager(this);
        // register system event aliases
        this.registerSystemEventAliases();

        // Prevention of control bar disappearing, when mouse is over it and option preventControlBarDisappearingByMouseOver is active
        if(this.options().preventControlBarDisappearingByMouseOver){
            this.preventControlBarDisappearingByMouseOver()
        }

        // Activate iFrame mode only, when player is inside a iFrame
        if(this.options().autoEnableIFrameMode  && Dom.isInFrame()){
            this.iFrameMode = true;
        }

        // Optimize player behavior, when iFrame mode is active
        if(this.iFrameMode){
            this.optimizeIFrameEmbedding();
        }
    }

    /**
     * Register defined systemEventAliases
     */
    protected registerSystemEventAliases(){
        for (let systemEventName in this.systemEventAliases) {
            let aliasHookName = this.systemEventAliases[systemEventName];
            this.eventManager.registerAlias(systemEventName, aliasHookName);
        }
    }

    /**
     * Prevent disappearing of control bar, when mouse is over control bar (override user inactivity event of video.js)
     */
    protected preventControlBarDisappearingByMouseOver(){
        // Wait until control bar is initialized
        this.eventManager.addListener<SkyClickPlayerEventType>('READY',() => {
            let className = 'vjs-prevent-control-bar-disappearing-by-mouse-over';
            let controlBarEl = this.getChild('controlBar').el();

            let mouseIsOverControlBar = false;

            // Add listeners on control bar, to add a class to the main element, when mouse is over control bar.
            // This helper class is used, that we can bridge the time between triggered 'userinactive' event and reactivation below with 'reportUserActivity'.
            // So we prevent a flickering of the elements, because the 'userinactive' mode is for a very short moment active.
            EventUtilWrapper.on(controlBarEl,'mouseover',() => {
                this.addClass(className);
                mouseIsOverControlBar = true;
            });
            EventUtilWrapper.on(controlBarEl,'mouseleave',() => {
                this.removeClass(className);
                mouseIsOverControlBar = false;
            });

            // Listen for USER_INACTIVE event.
            // Reset the the mode to 'USER_ACTIVE' and stop the event propagation, when mouse is over control bar
            this.eventManager.addListener<SkyClickPlayerEventType>('USER_INACTIVE',(event:any) => {
                if(mouseIsOverControlBar){
                    // When mouse is over control ...
                    // Stop forwarding of 'userinactive' event to other listeners (e.g. vtt thumbnail plugin, that will remove thumbnail)
                    event.stopImmediatePropagation();
                    // Trigger by code again 'useractive' mode (That the class of main element is also again correct)
                    this.reportUserActivity()
                }
            })
        })
    }

    // Optimize code, when player is embedded as iFrame
    protected optimizeIFrameEmbedding(){
        if(this.iFrameMode){
            // Run this code, when player is inside a iFrame
            // When no "base" element exists, add one, that cause that every link will be opened in parent (main) window)
            if(!Dom.$('base')){
                let baseEl = Dom.createEl('base',undefined,{
                    target: '_parent'
                });
                // Add head element, when not exists
                !Dom.$('head') && Dom.appendContent(Dom.$('html'), Dom.createEl('head'));
                // Add base element to head
                Dom.appendContent(Dom.$('head'), baseEl);
            }
        }
    }

    /**
     * Overwrite src method to trigger a event, when source has been changed
     * @param newSource
     * @returns {any}
     */
    public src(newSource:string | VideoJSSource | VideoJSSource[]):SkyClickPlayer{
        let result = super.src.apply(this, arguments);
        this.eventManager.trigger<SkyClickPlayerEventType>('SOURCE_CHANGED',this.currentSrc());
        return result;
    }

}

// Run static functions of plugin & tech decorators and custom plugins & tech
hookManagerInstance.addOnce<SkyClickPlayerHookType>('STATIC_ONCE_INIT',function(){

    ComponentManager.bootingStaticApi();

    PluginManager.bootingStaticApi();

    TechManager.bootingStaticApi();

    /*
    Here we delay all custom auto starts of plugins to ensure, that first all core plugins are started,
    before the custom auto starts of plugins will be executed.
    We packing this function call in a BEFORE_SETUP hook, because some core plugin decorators extend the default plugin options.
    Then we need the final default options, to decide which are custom auto starts of plugins.
    The plugin decorators need also the BEFORE_SETUP hook, but in this moment/this place of execution we are behind all the BEFORE_SETUP hooks of plugin decorators + custom plugin
    and will be therefore run at the end of all BEFORE_SETUP hooks. And exactly this is what we want and need.
    We are behind each other BEFORE_SETUP hook, because all BEFORE_SETUP hooks will be registered in the lines before.
    */
    hookManagerInstance.add<PlayerComponentHookType>('BEFORE_SETUP', (videoEl:Element, options: any) => {
        return SkyClickPlayer.delayCustomAutoStartOfPlugins(options);
    });


    // Register once the plugin, that complete the boot of SkyClickPlayer instance.
    // For more information check comment in this function.
    SkyClickPlayer.registerBootSkyClickInstancePlugin();

});



// WEBPACK FOOTER //
// ./src/app/components/custom/SkyClickPlayer.ts